import { Platform } from 'react-native'
import { requireOptionalNativeModule } from 'expo'
import Constants from 'expo-constants'

interface ExpoInAppUpdatesModule {
  FLEXIBLE: number
  checkForUpdate: () => Promise<{
    updateAvailable: boolean
    storeVersion?: string
    flexibleAllowed?: boolean
  }>
  startUpdate: (updateType?: number) => Promise<boolean>
}

export type AppUpdateCheckResult =
  | { status: 'unsupported' }
  | { status: 'up-to-date' }
  | {
      status: 'available'
      currentVersion: string
      latestVersion: string
      platform: 'android' | 'ios'
      flexibleAllowed?: boolean
    }

interface AppIdentity {
  currentVersion: string
  buildFlavour: string | null
}

function getExpoInAppUpdates(): ExpoInAppUpdatesModule | null {
  return requireOptionalNativeModule<ExpoInAppUpdatesModule>('ExpoInAppUpdates')
}

function getAppIdentity(): AppIdentity | null {
  const currentVersion = Constants.expoConfig?.version?.trim()
  const buildFlavour =
    (
      Constants.expoConfig?.extra?.build as
        | {
            flavour?: string
          }
        | undefined
    )?.flavour?.trim() ?? null

  if (!currentVersion) {
    return null
  }

  return {
    currentVersion,
    buildFlavour,
  }
}

export async function checkForAvailableAppUpdate(): Promise<AppUpdateCheckResult> {
  if (Platform.OS === 'web') {
    return { status: 'unsupported' }
  }

  const appIdentity = getAppIdentity()
  if (appIdentity == null) {
    return { status: 'unsupported' }
  }

  if (appIdentity.buildFlavour !== 'prod') {
    return { status: 'unsupported' }
  }

  if (Platform.OS !== 'ios' && Platform.OS !== 'android') {
    return { status: 'unsupported' }
  }

  const expoInAppUpdates = getExpoInAppUpdates()
  if (expoInAppUpdates == null) {
    return { status: 'unsupported' }
  }

  const update = await expoInAppUpdates.checkForUpdate()
  if (!update.updateAvailable) {
    return { status: 'up-to-date' }
  }

  const latestVersion = update.storeVersion?.trim()
  if (!latestVersion) {
    return { status: 'unsupported' }
  }

  return {
    status: 'available',
    currentVersion: appIdentity.currentVersion,
    latestVersion,
    platform: Platform.OS,
    ...(Platform.OS === 'android'
      ? { flexibleAllowed: update.flexibleAllowed === true }
      : {}),
  }
}

export async function startAvailableAppUpdate(): Promise<boolean> {
  const appIdentity = getAppIdentity()
  if (
    Platform.OS === 'web' ||
    (Platform.OS !== 'ios' && Platform.OS !== 'android') ||
    appIdentity?.buildFlavour !== 'prod'
  ) {
    return false
  }

  const expoInAppUpdates = getExpoInAppUpdates()
  if (expoInAppUpdates == null) {
    return false
  }

  return await expoInAppUpdates.startUpdate(
    Platform.OS === 'android' ? expoInAppUpdates.FLEXIBLE : undefined,
  )
}
